import { Component } from "@angular/core";
import { Button } from "primeng/button";
import { SharedComponentsModule } from "@/components/shared/shared-components.module";
import { InputSwitchModule } from "primeng/inputswitch";
import { CheckboxModule } from "primeng/checkbox";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MenuItem, MenuItemCommandEvent, MessageService } from "primeng/api";
import { ToastModule } from "primeng/toast";
import { ToastService } from "@/_services/ToastService";
import { SplitButtonModule } from "primeng/splitbutton";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { FormWidgetModule } from "@/components/shared/modules/form-widget/form-widget.module";
import {
    AutoCompleteCompleteEvent,
    AutoCompleteModule,
} from "primeng/autocomplete";
import { CommonModule } from "@angular/common";
import { Activity, ActivityType } from "@/_models/activity.interface";
import { ActivityTimelineComponent } from "@/components/shared/activity-timeline/activity-timeline.component";
import { TagModule } from "primeng/tag";
import { MessageModule } from "primeng/message";
import { DetailPageHeaderComponent } from "@/components/shared/detail-page-header/detail-page-header.component";
import {
    DETAIL_HEADER_LABELS,
    DetailHeaderMetadata,
    DetailHeaderMenuItem,
} from "@/components/shared/detail-page-header/detail-page-header.model";
import {
    ProcessStep,
    ProcessStepperComponent,
} from "@/components/shared/process-stepper/process-stepper.component";
import { ProcessStepContentDirective } from "@/components/shared/process-stepper/process-step-content.directive";
import {
    TabbedContentPaneComponent,
    TabPaneItem,
} from "@/components/shared/tabbed-content-pane/tabbed-content-pane.component";
import { TabContentDirective } from "@/components/shared/tabbed-content-pane/tab-content.directive";

@Component({
    selector: "app-playground",
    standalone: true,
    imports: [
        Button,
        SharedComponentsModule,
        ActivityTimelineComponent,
        InputSwitchModule,
        CheckboxModule,
        ReactiveFormsModule,
        ToastModule,
        SplitButtonModule,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        FormWidgetModule,
        AutoCompleteModule,
        CommonModule,
        TagModule,
        MessageModule,
        DetailPageHeaderComponent,
        ProcessStepperComponent,
        ProcessStepContentDirective,
        TabbedContentPaneComponent,
        TabContentDirective,
    ],
    providers: [MessageService],
    templateUrl: "./playground.component.html",
    styleUrl: "./playground.component.scss",
})
export class PlaygroundComponent {
    toastOptions: MenuItem[] = [
        {
            label: "Success",
            command: () => this.showToast("success"),
        },
        {
            label: "Default Toast",
            command: () => this.showToast("info"),
        },
        {
            label: "Warning toast",
            command: () => this.showToast("warn"),
        },
        {
            label: "Error Toast",
            command: () => this.showToast("error"),
        },
    ];
    suggestions: string[];
    constructor(
        private messageService: MessageService,
        private toastr: ToastService,
    ) {}

    // Process stepper demo (see the #2514 stepped-navigation pages for real
    // usage: evaluation-overview + issue-editor-v2)
    stepperSteps: ProcessStep[] = [
        { id: "details", label: "Details" },
        { id: "analysis", label: "Analysis" },
        { id: "strategy", label: "Strategy", alert: true },
        { id: "acceptance", label: "Acceptance" },
    ];
    activeStepId = "details";

    railTabs: TabPaneItem[] = [
        { id: "activity", label: "Activity" },
        { id: "linkedItems", label: "Linked Items", badge: 3 },
    ];

    checkboxControl = new FormControl<boolean>(false);
    searchControl: FormControl = new FormControl<string>("");
    multiSelectControl: FormControl = new FormControl<string[]>([]);

    readonly searchableItems = [
        "Apple",
        "Banana",
        "Cherry",
        "Date",
        "Elderberry",
        "Fig",
    ];
    selectedSuggestion: any;

    // Sample flight data for table
    sampleFlights = Array.from({ length: 15 }, (_, i) => ({
        _id: `flight-${i + 1}`,
        flightDate: new Date(2024, 0, i + 1),
        departure_airport: { code: `DEP${i + 1}`, name: `Departure ${i + 1}` },
        arrival_airport: { code: `ARR${i + 1}`, name: `Arrival ${i + 1}` },
        aircraft: { designation: `A${i + 1}`, registration: `REG-${i + 1}` },
        customIdentifier: `FL-${i + 1}`,
        status: ["ready", "in progress", "complete", "pending", "approved"][
            i % 5
        ],
        pic: { firstName: `PIC${i + 1}`, lastName: "Pilot" },
        sic:
            i % 2 === 0
                ? { firstName: `SIC${i + 1}`, lastName: "Pilot" }
                : null,
    }));

    // Selected flights for checkbox selection
    selectedFlights: any[] = [];

    // Sample activity items for the shared activity timeline (matches [parentId]/[parentType] in template)
    sampleActivities: Activity[] = [
        {
            _id: "1",
            user: {
                _id: "user-1",
                firstName: "Alex",
                lastName: "Pilot",
            } as any,
            date: new Date().toISOString(),
            type: ActivityType.CREATED,
            parentId: "playground-parent",
            parentType: "frat",
            data: {
                scoreRange: { canonicalName: "green" },
                score: 10,
            },
        },
        {
            _id: "2",
            user: {
                _id: "user-2",
                firstName: "Jamie",
                lastName: "Reviewer",
            } as any,
            date: new Date().toISOString(),
            type: ActivityType.SUBMITTED,
            parentId: "playground-parent",
            parentType: "frat",
            data: {},
        },
        {
            _id: "3",
            user: {
                _id: "user-1",
                firstName: "Alex",
                lastName: "Pilot",
            } as any,
            date: new Date().toISOString(),
            type: ActivityType.COMMENT,
            parentId: "playground-parent",
            parentType: "frat",
            data: {
                comment: [
                    { type: "text", value: "Example comment ", id: null },
                    { type: "user", value: "@Jamie", id: "user-2" },
                ],
            },
        },
    ];

    onRowSelect(event: any) {
        console.log("Row selected:", event.data);
    }

    onRowUnselect(event: any) {
        console.log("Row unselected:", event.data);
    }

    onSelectAll(event: any) {
        console.log("Select all:", event.checked);
    }

    search(event: AutoCompleteCompleteEvent) {
        console.log(event);
        this.suggestions = this.searchableItems.filter((item) => {
            return item.toLowerCase().includes(event.query.toLowerCase());
        });
    }

    showToast(level: string = "info") {
        switch (level) {
            case "success":
                return this.toastr.success("Success message here", "", {
                    disableTimeOut: true,
                });
            case "info":
                return this.toastr.info("Something informative", "Info");
            case "warn":
                return this.toastr.warning(
                    "You might wanna check this out",
                    "Warning",
                    {
                        disableTimeOut: true,
                    },
                );
            case "error":
                return this.toastr.error("Something went wrong", "Error");
        }
    }

    clearSearch() {
        this.searchControl.patchValue("");
    }

    headerMetadata: DetailHeaderMetadata = [
        { label: DETAIL_HEADER_LABELS.created, value: "Oct 25, 2025" },
        { label: DETAIL_HEADER_LABELS.creator, value: "M. Sidler" },
        {
            label: DETAIL_HEADER_LABELS.owner,
            value: "M. Sidler",
            action: () => this.toastr.info("Owner edit clicked", "Playground"),
        },
        {
            label: DETAIL_HEADER_LABELS.reassess,
            value: "Unset",
            action: () => this.toastr.info("Reassess clicked", "Playground"),
        },
    ];

    headerMetadataNoReassess: DetailHeaderMetadata = [
        { label: DETAIL_HEADER_LABELS.created, value: "Oct 25, 2025" },
        { label: DETAIL_HEADER_LABELS.creator, value: "M. Sidler" },
        { label: DETAIL_HEADER_LABELS.owner, value: "M. Sidler" },
    ];

    headerMetadataGroupStyle: DetailHeaderMetadata = [
        { label: DETAIL_HEADER_LABELS.created, value: "Oct 25, 2025" },
        { label: "Type", value: "Department" },
        { label: "Members", value: "12" },
    ];

    headerMenuItems: DetailHeaderMenuItem[] = [
        {
            label: "Export",
            iconName: "icon-export-pdf",
            action: () => this.toastr.info("Export clicked", "Playground"),
        },
        {
            label: "Archive",
            iconName: "icon-archive",
            action: () => this.toastr.info("Archive clicked", "Playground"),
        },
        {
            label: "Delete",
            iconName: "icon-delete",
            danger: true,
            action: () => this.toastr.info("Delete clicked", "Playground"),
        },
    ];

    onHeaderDemo(what: string) {
        this.toastr.info(`${what} clicked`, "Playground");
    }
}
